import { useState } from 'react';
import { IconClock, IconCalendarEvent } from '@tabler/icons-react';
import { Avatar, Badge, Box, Button, Card, DatePicker, Group, ScrollArea, Stack, Text, Title } from '@/components/ui';
import { mockEvents, generateTimeSlots, CalendarEvent } from '../mockData/calendar';

const formatTime = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
};

const isSameDay = (date1: Date, date2: Date) => {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

const hours = [9, 10, 11, 12, 13, 14, 15, 16, 17];

export default function SchedulePage() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const dayEvents = mockEvents.filter(e => isSameDay(e.start, selectedDate) && !e.allDay);

  // Calculate event position within the schedule
  const getEventPosition = (event: CalendarEvent) => {
    const startHour = event.start.getHours() + event.start.getMinutes() / 60;
    const endHour = event.end.getHours() + event.end.getMinutes() / 60;
    const top = (startHour - 9) * 60; // 60px per hour
    const height = (endHour - startHour) * 60;
    return { top, height };
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Schedule</Title>
          <Text c="dimmed" size="sm">View your daily schedule</Text>
        </Box>
        <Button leftSection={<IconCalendarEvent size={16} />}>New Event</Button>
      </Group>

      <Group align="flex-start" gap="lg">
        {/* Date Picker */}
        <Card w={300}>
          <Text fw={600} mb="md">Select Date</Text>
          <DatePicker
            value={selectedDate}
            onChange={(date) => date && setSelectedDate(date)}
          />
          <Box mt="md">
            <Text size="sm" c="dimmed" mb="xs">{dayEvents.length} events today</Text>
            {dayEvents.slice(0, 3).map(event => (
              <Group key={event.id} gap="xs" mb="xs">
                <Box
                  w={8}
                  h={8}
                  style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${event.color}-filled)` }}
                />
                <Text size="xs">{event.title}</Text>
              </Group>
            ))}
          </Box>
        </Card>

        {/* Schedule View */}
        <Card style={{ flex: 1 }}>
          <Group justify="space-between" mb="md">
            <Text fw={600}>
              {new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(selectedDate)}
            </Text>
            <Group gap="xs">
              <IconClock size={16} color="var(--mantine-color-dimmed)" />
              <Text size="sm" c="dimmed">9 AM - 6 PM</Text>
            </Group>
          </Group>

          <ScrollArea h={500}>
            <Box style={{ position: 'relative', minHeight: hours.length * 60 }}>
              {/* Hour grid lines */}
              {hours.map(hour => (
                <Group
                  key={hour}
                  gap="sm"
                  style={{
                    position: 'absolute',
                    top: (hour - 9) * 60,
                    left: 0,
                    right: 0,
                    borderTop: '1px solid var(--mantine-color-gray-3)',
                  }}
                >
                  <Text size="xs" c="dimmed" w={50}>
                    {hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`}
                  </Text>
                </Group>
              ))}

              {/* Events */}
              {dayEvents.map(event => {
                const { top, height } = getEventPosition(event);
                return (
                  <Box
                    key={event.id}
                    style={{
                      position: 'absolute',
                      top: top,
                      left: 60,
                      right: 10,
                      height: Math.max(height, 30),
                      backgroundColor: `var(--mantine-color-${event.color}-light)`,
                      borderLeft: `4px solid var(--mantine-color-${event.color}-filled)`,
                      borderRadius: 4,
                      padding: 8,
                      overflow: 'hidden',
                    }}
                  >
                    <Text size="sm" fw={500} lineClamp={1}>{event.title}</Text>
                    <Text size="xs" c="dimmed">
                      {formatTime(event.start)} - {formatTime(event.end)}
                    </Text>
                    {event.location && height > 50 && (
                      <Text size="xs" c="dimmed">{event.location}</Text>
                    )}
                  </Box>
                );
              })}

              {/* Current time indicator */}
              {isSameDay(selectedDate, new Date()) && (
                <Box
                  style={{
                    position: 'absolute',
                    top: (new Date().getHours() + new Date().getMinutes() / 60 - 9) * 60,
                    left: 50,
                    right: 0,
                    height: 2,
                    backgroundColor: 'var(--mantine-color-red-filled)',
                    zIndex: 10,
                  }}
                >
                  <Box
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: -4,
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: 'var(--mantine-color-red-filled)',
                    }}
                  />
                </Box>
              )}
            </Box>
          </ScrollArea>
        </Card>
      </Group>
    </Stack>
  );
}
